import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Button, Flex, Skeleton, Space, Tooltip } from 'antd';
import { LikeOutlined, RedoOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import styled from '@emotion/styled';

import AlertTooltip from 'components/AlertTooltip/AlertTooltip';
import useTimeoutValue from 'hooks/useTimeoutValue';
import { RESET_AUTHORIZATION_COUNTERS } from 'mutations/transactions';
import { AUTHORIZATION_COUNTERS } from 'queries/transactions';

const CountSkeleton = styled(Skeleton)`
  min-width: 1ch;
  ul {
    margin-bottom: 0;
  }
`;

const CountContainer = styled(Flex)`
  padding: 0 var(--spacing-sm);
  border: var(--border);
  border-right: 0;
  border-radius: 6px;
  border-start-end-radius: 0;
  border-end-end-radius: 0;
`;

const DeclinedAuthorizationControl = () => {
  const { accountId } = useParams();
  const [declinedCount, setDeclineCount] = useState('-');
  const [showLoadFailure, setShowLoadFailure] = useTimeoutValue(false);
  const [showResetFailure, setShowResetFailure] = useTimeoutValue(false);
  const [showSuccess, setShowSuccess] = useTimeoutValue(false);

  const [getCounters, countersQuery] = useLazyQuery(AUTHORIZATION_COUNTERS);

  useEffect(() => {
    // upgrade to useEffectEvent
    const fetchDeclineCount = async () => {
      try {
        const response = await getCounters({
          variables: { accountId },
        });
        setDeclineCount(
          response.data.accountDailyAuthorizationCounters.declined.count,
        );
      } catch {
        setShowLoadFailure(true);
      }
    };

    fetchDeclineCount();
  }, [accountId]);

  const [resetAuthorizationCounters, resetMutation] = useMutation(
    RESET_AUTHORIZATION_COUNTERS,
    {
      variables: { accountId },
      onCompleted: () => {
        setDeclineCount(0);
        setShowSuccess(true);
      },
      onError: () => {
        setShowResetFailure(true);
      },
    },
  );

  const renderButton = () => {
    let button = (
      <Button
        data-testid="reset-authorization-counters-button"
        icon={<RedoOutlined />}
        disabled={countersQuery.loading}
        loading={resetMutation.loading}
        onClick={resetAuthorizationCounters}
      ></Button>
    );

    if (showLoadFailure || showResetFailure) {
      const action = showLoadFailure ? 'load' : 'reset';
      button = (
        <AlertTooltip
          type="error"
          open={true}
          placement="topRight"
          title={`Failed to ${action} authorization counters`}
        >
          {button}
        </AlertTooltip>
      );
    } else {
      button = (
        <Tooltip
          open={showSuccess || undefined}
          placement="topRight"
          title={() => {
            return showSuccess ? (
              <span>
                Decline count reset <LikeOutlined />
              </span>
            ) : (
              'Reset declined authorization count'
            );
          }}
        >
          {button}
        </Tooltip>
      );
    }
    return button;
  };

  return (
    <Space.Compact>
      <CountContainer gap="var(--spacing-xxs)" align="center">
        <span>Declines:</span>
        {countersQuery.loading ? (
          <CountSkeleton
            active={true}
            title={false}
            paragraph={{ rows: [{ width: '1rem' }] }}
          />
        ) : (
          declinedCount
        )}
      </CountContainer>
      {renderButton()}
    </Space.Compact>
  );
};

export default DeclinedAuthorizationControl;
